import { useState, useEffect, useRef } from 'react';
import type { Message } from '../types';
import { getMessages } from '../api';
import { MessageCard } from './MessageCard';

export function CardViewer() {
  const [messages, setMessages] = useState<Message[]>([]);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const touchStartX = useRef(0);
  const [perPage] = useState(() => (window.innerWidth < 640 ? 3 : 6));

  useEffect(() => {
    getMessages()
      .then(setMessages)
      .catch(() => setError('Failed to load messages — try refreshing'))
      .finally(() => setLoading(false));
  }, []);

  const totalPages = Math.max(1, Math.ceil(messages.length / perPage));
  const pageMessages = messages.slice(page * perPage, (page + 1) * perPage);
  const globalOffset = page * perPage;

  const prevPage = () => setPage(p => Math.max(0, p - 1));
  const nextPage = () => setPage(p => Math.min(totalPages - 1, p + 1));

  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
  };
  const handleTouchEnd = (e: React.TouchEvent) => {
    const diff = touchStartX.current - e.changedTouches[0].clientX;
    if (Math.abs(diff) > 50) diff > 0 ? nextPage() : prevPage();
  };

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight') nextPage();
      if (e.key === 'ArrowLeft') prevPage();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [totalPages]);

  if (loading) {
    return (
      <div className="flex justify-center items-center py-24 text-slate-500">
        Loading messages...
      </div>
    );
  }
  if (error) {
    return <div className="text-center py-16 text-red-400">{error}</div>;
  }
  if (messages.length === 0) {
    return (
      <div className="text-center py-16 text-slate-500">
        <p className="text-lg">No messages yet.</p>
        <p className="text-sm mt-1">Be the first to leave one!</p>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto px-4 py-8">
      <div onTouchStart={handleTouchStart} onTouchEnd={handleTouchEnd}>
        {pageMessages.map((msg, i) => (
          <MessageCard
            key={msg.id}
            message={msg}
            align={(globalOffset + i) % 2 === 0 ? 'left' : 'right'}
          />
        ))}
      </div>

      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-4 mt-8">
          <button
            onClick={prevPage}
            disabled={page === 0}
            className="w-10 h-10 rounded-lg bg-[#1e1e38] text-slate-300 disabled:opacity-25 hover:bg-[#2d2d4e] transition-colors text-lg"
            aria-label="Previous page"
          >
            ←
          </button>

          <div className="flex gap-2" role="tablist" aria-label="Pages">
            {Array.from({ length: totalPages }, (_, i) => (
              <button
                key={i}
                onClick={() => setPage(i)}
                role="tab"
                aria-selected={i === page}
                className={`w-2.5 h-2.5 rounded-full transition-colors ${
                  i === page ? 'bg-violet-500' : 'bg-slate-700 hover:bg-slate-500'
                }`}
              />
            ))}
          </div>

          <button
            onClick={nextPage}
            disabled={page === totalPages - 1}
            className="w-10 h-10 rounded-lg bg-[#1e1e38] text-slate-300 disabled:opacity-25 hover:bg-[#2d2d4e] transition-colors text-lg"
            aria-label="Next page"
          >
            →
          </button>
        </div>
      )}

      <p className="text-center text-slate-600 text-sm mt-4">
        {messages.length} message{messages.length !== 1 ? 's' : ''}
      </p>
    </div>
  );
}
